import { SettingsManager } from "@viamap/viamap2-common";
import { ESInterfaceJWTAuth } from "src/compentsModus/ESInterfaceJWTAuth";
import { JPBManager } from "./JPBManager";
import JPBRegions from "./JPBRegions.json";
import {
  JPBDynamicBucketRule,
  JPBExpFit,
  JPBIndexBuilderOptions,
  JPBIndexComputationMethod,
  JPBIndexDocument,
  JPBHovedstadenGroupingMode,
  JPBIndexInterval,
  JPBIndexPeriod,
  JPBIndexSmoothingMode,
  JPBIndexStatistic,
} from "./JPBIndexBuilderTypes";

type MunicipalityItem = {
  landsdelNr: number;
  landsdel: string;
  kommunenr: number;
  kommunenavn: string;
};

type JPBSource = {
  dato?: number;
  pris?: number;
  m2total?: number;
  komkode?: string | number;
  prisDriftsbygning?: number;
  prisStuehus?: number;
  status?: string;
  reasonCode?: string;
  reasonText?: string;
};

type JPBHit = {
  _source?: JPBSource;
  sort?: unknown[];
  _seq_no?: number;
};

type PeriodTemplate = {
  key: string;
  start: number;
  end: number;
};

type ComputeAllProgress = {
  landsdel: string;
  current: number;
  total: number;
};

type JPBRawTransactionRow = {
  dato: number;
  pris: number;
  m2total: number;
  komkode: string;
  landsdel: string;
  prisDriftsbygning: number | null;
  prisStuehus: number | null;
};

type JPBRowsCache = {
  rows: JPBRawTransactionRow[];
  sourceIndexName: string;
  yearsBack: number;
  referenceDateMs: number;
  referenceFromMetadata: boolean;
  fromTimestamp: number;
  toTimestamp: number;
  fetchedAt: number;
};

type JPBIndexExportPeriod = JPBIndexPeriod & {
  fitFactor: number | null;
  fitValue: number | null;
};

type JPBIndexExportDocument = Omit<JPBIndexDocument, "periods"> & {
  periods: JPBIndexExportPeriod[];
};

export type JPBIndexBuilderCacheInfo = {
  usedCache: boolean;
  sourceIndexName: string;
  yearsBack: number;
  referenceDateMs: number;
  rowsInMemory: number;
  fromTimestamp: number;
  toTimestamp: number;
  fetchedAt: number;
};

export type JPBIndexBuilderComputeAllResult = {
  documents: JPBIndexDocument[];
  cache: JPBIndexBuilderCacheInfo;
};

export type JPBIndexBuilderRawCheckedRow = {
  landsdel: string;
  komkode: string;
  datoMs: number;
  periodKey: string;
  m2total: number;
  rawSalePrice: number;
  rawPricePerM2: number;
  prisDriftsbygning: number | null;
  prisStuehus: number | null;
  hasBuildingValue: boolean;
  buildingAreaThresholdHa: number;
  pricePerM2Min: number | null;
  pricePerM2Max: number | null;
  subtractBuildingPrices: boolean;
  effectiveRawPrice: number;
  effectivePricePerM2: number;
};

const DEFAULT_TARGET_INDEX = "jpbindex";
const DEFAULT_YEARS_BACK = 5;
const MAX_PAGES = 5000;
const DEFAULT_FETCH_SIZE = 8000;
const MAX_FETCH_SIZE = 10000;

type DynamicWindowRule = {
  name: JPBDynamicBucketRule;
  halfWindowQuarters: number;
};

// Tune these constants to adjust dynamic bucket behavior.
const DYNAMIC_BUCKET_MIN_TX_COUNT = 30;
const DYNAMIC_BUCKET_MAX_RELATIVE_CI95 = 0.25;
const DYNAMIC_BUCKET_MAX_HALF_WINDOW_QUARTERS = 6;
const HOVEDSTADEN_LANDSDEL = "Hovedstaden";
const HOVEDSTADEN_GROUPING_TO_LANDSDELE: Record<JPBHovedstadenGroupingMode, string[]> = {
  none: [HOVEDSTADEN_LANDSDEL],
  withNordsjaelland: [HOVEDSTADEN_LANDSDEL, "Nordsjælland"],
  withNordsjaellandAndOestsjaelland: [HOVEDSTADEN_LANDSDEL, "Nordsjælland", "Østsjælland"],
};

function toDynamicBucketRuleName(halfWindowQuarters: number): JPBDynamicBucketRule {
  if (halfWindowQuarters === 1) return "quarterRolling";
  if (halfWindowQuarters === 3) return "halfYearRolling";
  if (halfWindowQuarters === 6) return "fullYearRolling";
  return `rollingWindow${halfWindowQuarters}`;
}

const DYNAMIC_BUCKET_WINDOWS: DynamicWindowRule[] = Array.from(
  { length: DYNAMIC_BUCKET_MAX_HALF_WINDOW_QUARTERS },
  (_, idx) => {
    const halfWindowQuarters = idx + 1;
    return {
      name: toDynamicBucketRuleName(halfWindowQuarters),
      halfWindowQuarters,
    };
  }
);

type DynamicWindowSelection = {
  values: number[];
  rule: DynamicWindowRule;
  relativeCi95: number | null;
};

type LogStatisticEstimate = {
  rawValue: number | null;
  logValue: number | null;
  standardError: number | null;
  txCount: number;
};

type QuarterlyShrinkagePrior = {
  rawValue: number | null;
  logValue: number | null;
  tau2: number | null;
};

const DEFAULT_COMPUTATION_METHOD: JPBIndexComputationMethod = "standard";
const SHRINKAGE_MIN_TAU2 = 1e-6;
const SHRINKAGE_MIN_LOCAL_TX_COUNT = 3;
const SHRINKAGE_MIN_REGIONAL_TX_COUNT_FOR_TAU2 = 3;

export class JPBIndexBuilderManager {
  private static rowsCache: JPBRowsCache | null = null;
  private static kommuneToLandsdelMap: Map<string, string> | null = null;

  static getLandsdelList(): string[] {
    const municipalities = (JPBRegions.municipalities || []) as MunicipalityItem[];
    const sorted = [...municipalities].sort((a, b) => {
      if (a.landsdelNr !== b.landsdelNr) return a.landsdelNr - b.landsdelNr;
      return a.landsdel.localeCompare(b.landsdel, "da");
    });
    const unique = new Set<string>();
    sorted.forEach((m) => unique.add(m.landsdel));
    return [...unique];
  }

  static getTargetIndexName(): string {
    return SettingsManager.getSystemSetting("jpb.indexBuilderIndex", DEFAULT_TARGET_INDEX).toLowerCase();
  }

  static invalidateRowsCache(): void {
    this.rowsCache = null;
  }

  static buildDocumentId(
    doc: Pick<
      JPBIndexDocument,
      | "computationMethod"
      | "landsdel"
      | "interval"
      | "statistic"
      | "smoothingMode"
      | "dynamicBucketGeneralization"
      | "hovedstadenGroupingMode"
      | "buildingAreaThresholdHa"
      | "pricePerM2Min"
      | "pricePerM2Max"
      | "subtractBuildingPrices"
    >
  ): string {
    const base = `${doc.landsdel}__${doc.interval}__${doc.statistic}`.replace(/\s+/g, "_");
    const suffixes: string[] = [];
    if (doc.computationMethod && doc.computationMethod !== DEFAULT_COMPUTATION_METHOD) suffixes.push("cm-qshr");
    if (doc.smoothingMode !== "none") suffixes.push(`sm-${doc.smoothingMode}`);
    if (doc.dynamicBucketGeneralization) suffixes.push("dbg-1");
    if (doc.landsdel === HOVEDSTADEN_LANDSDEL && doc.hovedstadenGroupingMode && doc.hovedstadenGroupingMode !== "none") {
      suffixes.push(
        doc.hovedstadenGroupingMode === "withNordsjaelland" ? "hg-ns" : "hg-ns-os"
      );
    }
    if (doc.buildingAreaThresholdHa > 0) suffixes.push(`ba-${doc.buildingAreaThresholdHa.toString().replace(".", "_")}ha`);
    if (typeof doc.pricePerM2Min === "number" && Number.isFinite(doc.pricePerM2Min)) {
      suffixes.push(`ppm2min-${doc.pricePerM2Min.toString().replace(".", "_")}`);
    }
    if (typeof doc.pricePerM2Max === "number" && Number.isFinite(doc.pricePerM2Max)) {
      suffixes.push(`ppm2max-${doc.pricePerM2Max.toString().replace(".", "_")}`);
    }
    if (doc.subtractBuildingPrices) suffixes.push("sb-1");
    if (!suffixes.length) return base;
    return `${base}__${suffixes.join("__")}`;
  }

  static async computeDocument(options: JPBIndexBuilderOptions): Promise<JPBIndexDocument> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache } = await this.getOrFetchRawRows(yearsBack, false);
    const rowsByLandsdel = this.groupRowsByLandsdel(rows);
    const computationRows = this.getRowsForComputationLandsdel(
      options.landsdel,
      rowsByLandsdel,
      options.hovedstadenGroupingMode
    );
    return this.buildDocumentFromRows(
      options.landsdel,
      computationRows,
      rowsByLandsdel,
      cache.toTimestamp,
      options
    );
  }

  static async computeAllDocuments(
    options: Omit<JPBIndexBuilderOptions, "landsdel">,
    onProgress?: (progress: ComputeAllProgress) => void,
    runOptions?: { forceRefresh?: boolean }
  ): Promise<JPBIndexBuilderComputeAllResult> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache, usedCache } = await this.getOrFetchRawRows(yearsBack, Boolean(runOptions?.forceRefresh));
    const rowsByLandsdel = this.groupRowsByLandsdel(rows);
    const landsdele = this.getLandsdelList();
    const docs: JPBIndexDocument[] = [];

    for (let i = 0; i < landsdele.length; i++) {
      const landsdel = landsdele[i];
      onProgress?.({ landsdel, current: i + 1, total: landsdele.length });
      const computationRows = this.getRowsForComputationLandsdel(
        landsdel,
        rowsByLandsdel,
        options.hovedstadenGroupingMode
      );
      const doc = this.buildDocumentFromRows(
        landsdel,
        computationRows,
        rowsByLandsdel,
        cache.toTimestamp,
        { ...options, landsdel, yearsBack }
      );
      docs.push(doc);
    }

    return { documents: docs, cache: this.toCacheInfo(cache, usedCache) };
  }

  static async getRawCheckedRows(
    options: Omit<JPBIndexBuilderOptions, "landsdel"> & { landsdele?: string[] },
    runOptions?: { forceRefresh?: boolean }
  ): Promise<{ rows: JPBIndexBuilderRawCheckedRow[]; cache: JPBIndexBuilderCacheInfo }> {
    const yearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const { rows, cache, usedCache } = await this.getOrFetchRawRows(yearsBack, Boolean(runOptions?.forceRefresh));

    const selectedLandsdele =
      options.landsdele && options.landsdele.length > 0 ? new Set(options.landsdele) : null;
    const dynamicBucketGeneralization = this.resolveDynamicBucketGeneralization(options);
    const effectiveInterval = this.resolveComputationInterval(options);
    const requestedThresholdHa = Math.max(0, Number(options.buildingAreaThresholdHa) || 0);
    const { min: pricePerM2Min, max: pricePerM2Max } = this.getNormalizedPricePerM2Range(options.pricePerM2Min, options.pricePerM2Max);
    const subtractBuildingPrices = Boolean(options.subtractBuildingPrices);
    const openIntervalStartMs = this.getOpenIntervalStartMs(cache.toTimestamp, effectiveInterval);
    const out: JPBIndexBuilderRawCheckedRow[] = [];

    rows.forEach((row) => {
      if (selectedLandsdele && !selectedLandsdele.has(row.landsdel)) return;
      // Ignore transactions in the currently open (unfinished) interval.
      if (row.dato >= openIntervalStartMs) return;
      if (!(row.m2total > 0) || !(row.pris > 0)) return;

      const hasBuildingValue =
        (typeof row.prisDriftsbygning === "number" && row.prisDriftsbygning > 0) ||
        (typeof row.prisStuehus === "number" && row.prisStuehus > 0);
      const thresholdHa = this.getEffectiveBuildingAreaThresholdHa(
        row.landsdel,
        requestedThresholdHa,
        dynamicBucketGeneralization
      );
      const thresholdM2 = thresholdHa * 10000;
      const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || row.m2total >= thresholdM2;
      if (!buildingAreaOk) return;

      const buildingsPriceSum = (row.prisDriftsbygning ?? 0) + (row.prisStuehus ?? 0);
      const effectiveRawPrice = subtractBuildingPrices ? Math.max(0, row.pris - buildingsPriceSum) : row.pris;
      if (!(effectiveRawPrice > 0)) return;
      const effectivePricePerM2 = effectiveRawPrice / row.m2total;
      if (!this.isWithinPricePerM2Range(effectivePricePerM2, pricePerM2Min, pricePerM2Max)) return;

      out.push({
        landsdel: row.landsdel,
        komkode: row.komkode,
        datoMs: row.dato,
        periodKey: this.toPeriodKey(new Date(row.dato), effectiveInterval),
        m2total: row.m2total,
        rawSalePrice: row.pris,
        rawPricePerM2: row.pris / row.m2total,
        prisDriftsbygning: row.prisDriftsbygning,
        prisStuehus: row.prisStuehus,
        hasBuildingValue,
        buildingAreaThresholdHa: thresholdHa,
        pricePerM2Min,
        pricePerM2Max,
        subtractBuildingPrices,
        effectiveRawPrice,
        effectivePricePerM2,
      });
    });

    return { rows: out, cache: this.toCacheInfo(cache, usedCache) };
  }

  static async ensureTargetIndexExists(indexName?: string): Promise<string> {
    const targetIndex = (indexName || this.getTargetIndexName()).toLowerCase();
    const es = new ESInterfaceJWTAuth(targetIndex);
    try {
      await es.createIndex(targetIndex, {
        mappings: {
          properties: {
            landsdel: { type: "keyword" },
            computationMethod: { type: "keyword" },
            interval: { type: "keyword" },
            statistic: { type: "keyword" },
            smoothingMode: { type: "keyword" },
            dynamicBucketGeneralization: { type: "boolean" },
            hovedstadenGroupingMode: { type: "keyword" },
            buildingAreaThresholdHa: { type: "double" },
            pricePerM2Min: { type: "double" },
            pricePerM2Max: { type: "double" },
            subtractBuildingPrices: { type: "boolean" },
            yearsBack: { type: "integer" },
            generatedAt: { type: "long" },
            basePeriod: { type: "keyword" },
            periods: {
              type: "nested",
              properties: {
                periodKey: { type: "keyword" },
                periodStart: { type: "long" },
                periodEnd: { type: "long" },
                value: { type: "double" },
                factor: { type: "double" },
                txCount: { type: "integer" },
                dynamicBucketRule: { type: "keyword" },
                dynamicHalfWindowQuarters: { type: "integer" },
                dynamicRelativeCi95: { type: "double" },
                shrinkageWeight: { type: "double" },
                shrinkageRawValue: { type: "double" },
                shrinkagePriorValue: { type: "double" },
                fitFactor: { type: "double" },
                fitValue: { type: "double" },
              },
            },
            fitExp: {
              properties: {
                a: { type: "double" },
                b: { type: "double" },
                rSquared: { type: "double" },
                rmse: { type: "double" },
              },
            },
          },
        },
      });
    } catch (error: any) {
      const msg = `${error?.message || error || ""}`.toLowerCase();
      const isAlreadyExists =
        msg.includes("already_exists") ||
        msg.includes("resource_already_exists_exception") ||
        msg.includes("already exists");
      if (!isAlreadyExists) {
        throw error;
      }
    }
    return targetIndex;
  }

  static async uploadDocuments(documents: JPBIndexDocument[], indexName?: string): Promise<{ indexName: string; uploaded: number }> {
    const targetIndex = (indexName || this.getTargetIndexName()).toLowerCase();
    if (!documents.length) {
      return { indexName: targetIndex, uploaded: 0 };
    }

    await this.ensureTargetIndexExists(targetIndex);
    const es = new ESInterfaceJWTAuth(targetIndex);
    const exportDocs = this.toExportDocuments(documents);
    let uploaded = 0;
    const errors: string[] = [];

    for (let i = 0; i < exportDocs.length; i++) {
      const exportDoc = exportDocs[i];
      const docId = this.buildDocumentId(documents[i]);
      try {
        await es.updateDocument(docId, exportDoc);
        uploaded += 1;
      } catch (error: any) {
        errors.push(`${docId}: ${error?.message || String(error)}`);
        if (errors.length >= 3) break;
      }
    }

    if (errors.length > 0) {
      throw new Error(`Saved ${uploaded}/${exportDocs.length} documents. First errors: ${errors.join(" | ")}`);
    }

    return { indexName: targetIndex, uploaded };
  }

  static toExportJSON(documents: JPBIndexDocument[]): string {
    return JSON.stringify(this.toExportDocuments(documents), null, 2);
  }

  static toExportNDJSON(documents: JPBIndexDocument[], indexName?: string): string {
    const target = (indexName || this.getTargetIndexName()).toLowerCase();
    const exportDocs = this.toExportDocuments(documents);
    const lines: string[] = [];
    exportDocs.forEach((doc) => {
      lines.push(JSON.stringify({ index: { _index: target, _id: this.buildDocumentId(doc) } }));
      lines.push(JSON.stringify(doc));
    });
    return lines.join("\n") + "\n";
  }

  static computeExponentialFit(periods: JPBIndexPeriod[]): JPBExpFit | undefined {
    const points = periods
      .map((period, idx) => ({ x: idx, y: period.factor }))
      .filter((point) => typeof point.y === "number" && point.y > 0) as { x: number; y: number }[];

    if (points.length < 2) return undefined;

    const xMean = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const yLn = points.map((p) => Math.log(p.y));
    const yMean = yLn.reduce((sum, value) => sum + value, 0) / yLn.length;

    let numerator = 0;
    let denominator = 0;
    for (let i = 0; i < points.length; i++) {
      numerator += (points[i].x - xMean) * (yLn[i] - yMean);
      denominator += Math.pow(points[i].x - xMean, 2);
    }
    if (denominator === 0) return undefined;

    const b = numerator / denominator;
    const lnA = yMean - b * xMean;
    const a = Math.exp(lnA);

    let ssRes = 0;
    let ssTot = 0;
    let squaredErrorSum = 0;
    for (let i = 0; i < points.length; i++) {
      const predicted = lnA + b * points[i].x;
      ssRes += Math.pow(yLn[i] - predicted, 2);
      ssTot += Math.pow(yLn[i] - yMean, 2);
      const predictedValue = a * Math.exp(b * points[i].x);
      squaredErrorSum += Math.pow(points[i].y - predictedValue, 2);
    }
    const rSquared = ssTot <= 0 ? 1 : Math.max(0, Math.min(1, 1 - ssRes / ssTot));
    const rmse = Math.sqrt(squaredErrorSum / points.length);

    return { a, b, rSquared, rmse };
  }

  private static toExportDocuments(documents: JPBIndexDocument[]): JPBIndexExportDocument[] {
    const referenceTimestamp = Date.now();
    return documents.map((doc) => {
      const openIntervalStartMs = this.getOpenIntervalStartMs(referenceTimestamp, doc.interval);
      const filteredPeriods = doc.periods.filter((period) => period.periodStart < openIntervalStartMs);
      const fitForExport = doc.fitExp ? this.computeExponentialFit(filteredPeriods) : undefined;
      const resolvedBasePeriod =
        (doc.basePeriod && filteredPeriods.some((period) => period.periodKey === doc.basePeriod)
          ? doc.basePeriod
          : filteredPeriods.find((period) => typeof period.value === "number" && period.value > 0)?.periodKey) ?? null;
      const idxByPeriodKey = new Map(filteredPeriods.map((period, idx) => [period.periodKey, idx]));
      const baseIdx = resolvedBasePeriod ? idxByPeriodKey.get(resolvedBasePeriod) : 0;
      const basePeriodValue =
        (resolvedBasePeriod
          ? filteredPeriods.find((period) => period.periodKey === resolvedBasePeriod)?.value
          : filteredPeriods.find((period) => typeof period.value === "number" && period.value > 0)?.value) ?? null;
      const baseFitFactor =
        fitForExport && baseIdx !== undefined
          ? fitForExport.a * Math.exp(fitForExport.b * baseIdx)
          : null;

      const periods: JPBIndexExportPeriod[] = filteredPeriods.map((period, idx) => {
        const fitFactor = fitForExport ? fitForExport.a * Math.exp(fitForExport.b * idx) : null;
        const fitValue =
          typeof fitFactor === "number" &&
          Number.isFinite(fitFactor) &&
          typeof baseFitFactor === "number" &&
          Number.isFinite(baseFitFactor) &&
          baseFitFactor > 0 &&
          typeof basePeriodValue === "number" &&
          Number.isFinite(basePeriodValue) &&
          basePeriodValue > 0
            ? (fitFactor / baseFitFactor) * basePeriodValue
            : null;
        return {
          ...period,
          fitFactor,
          fitValue,
        };
      });

      return {
        ...doc,
        fitExp: fitForExport,
        basePeriod: resolvedBasePeriod,
        periods,
      };
    });
  }

  private static async getReferenceDateInfo(): Promise<{ date: Date; fromMetadata: boolean }> {
    return { date: new Date(), fromMetadata: false };
  }

  private static getKommuneToLandsdelMap(): Map<string, string> {
    if (this.kommuneToLandsdelMap) {
      return this.kommuneToLandsdelMap;
    }
    const municipalities = (JPBRegions.municipalities || []) as MunicipalityItem[];
    const map = new Map<string, string>();
    municipalities.forEach((m) => {
      map.set(String(m.kommunenr), m.landsdel);
    });
    this.kommuneToLandsdelMap = map;
    return map;
  }

  private static buildPeriodTemplate(fromDate: Date, toDate: Date, interval: JPBIndexInterval): PeriodTemplate[] {
    const periods: PeriodTemplate[] = [];
    let cursor = this.toPeriodStart(fromDate, interval);
    const toMs = toDate.getTime();

    while (cursor.getTime() <= toMs) {
      const start = new Date(cursor);
      const next = this.advancePeriod(start, interval);
      const end = new Date(next.getTime() - 1);
      periods.push({
        key: this.toPeriodKey(start, interval),
        start: start.getTime(),
        end: end.getTime(),
      });
      cursor = next;
    }

    return periods;
  }

  private static getFromTimestampForYearsBack(referenceTimestamp: number, yearsBack: number): number {
    const fromDate = new Date(referenceTimestamp);
    fromDate.setFullYear(fromDate.getFullYear() - yearsBack);
    return fromDate.getTime();
  }

  private static toPeriodStart(date: Date, interval: JPBIndexInterval): Date {
    const year = date.getFullYear();
    const month = date.getMonth();

    if (interval === "quarter") {
      const quarterStartMonth = Math.floor(month / 3) * 3;
      return new Date(year, quarterStartMonth, 1, 0, 0, 0, 0);
    }

    if (interval === "halfYear") {
      return new Date(year, month < 6 ? 0 : 6, 1, 0, 0, 0, 0);
    }

    return new Date(year, 0, 1, 0, 0, 0, 0);
  }

  private static getOpenIntervalStartMs(referenceTimestamp: number, interval: JPBIndexInterval): number {
    return this.toPeriodStart(new Date(referenceTimestamp), interval).getTime();
  }

  private static advancePeriod(startDate: Date, interval: JPBIndexInterval): Date {
    if (interval === "quarter") return new Date(startDate.getFullYear(), startDate.getMonth() + 3, 1, 0, 0, 0, 0);
    if (interval === "halfYear") return new Date(startDate.getFullYear(), startDate.getMonth() + 6, 1, 0, 0, 0, 0);
    return new Date(startDate.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
  }

  private static toPeriodKey(date: Date, interval: JPBIndexInterval): string {
    const year = date.getFullYear();
    if (interval === "quarter") {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${year}K${quarter}`;
    }
    if (interval === "halfYear") {
      const half = date.getMonth() < 6 ? 1 : 2;
      return `${year}H${half}`;
    }
    return `${year}`;
  }

  private static resolveComputationMethod(
    options: Pick<JPBIndexBuilderOptions, "computationMethod">
  ): JPBIndexComputationMethod {
    return options.computationMethod === "quarterlyShrinkage" ? "quarterlyShrinkage" : DEFAULT_COMPUTATION_METHOD;
  }

  private static resolveComputationInterval(
    options: Pick<JPBIndexBuilderOptions, "computationMethod" | "dynamicBucketGeneralization" | "interval">
  ): JPBIndexInterval {
    if (this.resolveComputationMethod(options) === "quarterlyShrinkage") {
      return "quarter";
    }
    return options.dynamicBucketGeneralization ? "quarter" : options.interval;
  }

  private static resolveComputationSmoothingMode(
    options: Pick<JPBIndexBuilderOptions, "computationMethod" | "dynamicBucketGeneralization" | "smoothingMode">
  ): JPBIndexSmoothingMode {
    if (this.resolveComputationMethod(options) === "quarterlyShrinkage") {
      return "none";
    }
    if (options.dynamicBucketGeneralization) {
      return "windowPlusMinusOne";
    }
    return options.smoothingMode;
  }

  private static resolveDynamicBucketGeneralization(
    options: Pick<JPBIndexBuilderOptions, "computationMethod" | "dynamicBucketGeneralization">
  ): boolean {
    if (this.resolveComputationMethod(options) === "quarterlyShrinkage") {
      return false;
    }
    return Boolean(options.dynamicBucketGeneralization);
  }

  private static isCopenhagenLandsdel(landsdel: string): boolean {
    const normalized = String(landsdel || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "aa");
    return normalized.includes("hovedstaden") || normalized.includes("storkobenhavn") || normalized === "kobenhavn";
  }

  private static getEffectiveBuildingAreaThresholdHa(
    landsdel: string,
    requestedThresholdHa: number,
    dynamicBucketGeneralization: boolean
  ): number {
    const normalizedThresholdHa = Math.max(0, Number(requestedThresholdHa) || 0);
    if (dynamicBucketGeneralization && this.isCopenhagenLandsdel(landsdel)) {
      return 0;
    }
    return normalizedThresholdHa;
  }

  private static getDocumentBuildingAreaThresholdHa(
    landsdel: string,
    requestedThresholdHa: number,
    dynamicBucketGeneralization: boolean,
    hovedstadenGroupingMode?: JPBHovedstadenGroupingMode
  ): number {
    const normalizedThresholdHa = Math.max(0, Number(requestedThresholdHa) || 0);
    const effectiveGroupingMode = hovedstadenGroupingMode || "none";
    if (
      landsdel === HOVEDSTADEN_LANDSDEL &&
      dynamicBucketGeneralization &&
      effectiveGroupingMode !== "none"
    ) {
      return normalizedThresholdHa;
    }
    return this.getEffectiveBuildingAreaThresholdHa(
      landsdel,
      normalizedThresholdHa,
      dynamicBucketGeneralization
    );
  }

  private static normalizePricePerM2Bound(value: unknown): number | null {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
  }

  private static getNormalizedPricePerM2Range(
    minValue: unknown,
    maxValue: unknown
  ): { min: number | null; max: number | null } {
    const min = this.normalizePricePerM2Bound(minValue);
    const max = this.normalizePricePerM2Bound(maxValue);
    if (min !== null && max !== null && min > max) {
      return { min: max, max: min };
    }
    return { min, max };
  }

  private static isWithinPricePerM2Range(
    pricePerM2: number,
    minValue: number | null,
    maxValue: number | null
  ): boolean {
    if (!(typeof pricePerM2 === "number") || !Number.isFinite(pricePerM2) || pricePerM2 <= 0) return false;
    if (minValue !== null && pricePerM2 < minValue) return false;
    if (maxValue !== null && pricePerM2 > maxValue) return false;
    return true;
  }

  private static calculateStatistic(values: number[], statistic: JPBIndexStatistic): number | null {
    if (!values.length) return null;
    if (statistic === "mean") {
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    if (n % 2 === 1) return sorted[(n - 1) / 2];
    return (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
  }

  private static getValuesForSmoothingMode(
    periodTemplate: PeriodTemplate[],
    groupedValues: Map<string, number[]>,
    idx: number,
    smoothingMode: JPBIndexSmoothingMode
  ): number[] {
    const current = groupedValues.get(periodTemplate[idx].key) || [];
    if (smoothingMode === "none") {
      return current;
    }

    // Sliding window (t-1, t, t+1): use transaction-level pooling, not median-of-medians.
    return this.collectValuesInQuarterWindow(periodTemplate, groupedValues, idx, 1);
  }

  private static collectValuesInQuarterWindow(
    periodTemplate: PeriodTemplate[],
    groupedValues: Map<string, number[]>,
    idx: number,
    halfWindowQuarters: number
  ): number[] {
    const out: number[] = [];
    const startIdx = Math.max(0, idx - halfWindowQuarters);
    const endIdx = Math.min(periodTemplate.length - 1, idx + halfWindowQuarters);
    for (let cursor = startIdx; cursor <= endIdx; cursor++) {
      const values = groupedValues.get(periodTemplate[cursor].key) || [];
      if (values.length) out.push(...values);
    }
    return out;
  }

  private static quantileFromSorted(sortedValues: number[], q: number): number {
    if (!sortedValues.length) return Number.NaN;
    const boundedQ = Math.max(0, Math.min(1, q));
    const position = (sortedValues.length - 1) * boundedQ;
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    if (lower === upper) return sortedValues[lower];
    const weight = position - lower;
    return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
  }

  private static estimateRelativeCi95(
    values: number[],
    statistic: JPBIndexStatistic,
    centralValue: number | null
  ): number | null {
    if (!values.length || !(typeof centralValue === "number") || !Number.isFinite(centralValue) || centralValue <= 0) {
      return null;
    }
    if (values.length < 2) {
      return Number.POSITIVE_INFINITY;
    }

    const standardError = this.calculateStatisticStandardError(values, statistic);

    if (!Number.isFinite(standardError)) {
      return Number.POSITIVE_INFINITY;
    }

    const ciHalfWidth = 1.96 * standardError;
    return ciHalfWidth / Math.abs(centralValue);
  }

  private static calculateStatisticStandardError(values: number[], statistic: JPBIndexStatistic): number {
    if (values.length < 2) {
      return Number.POSITIVE_INFINITY;
    }
    const n = values.length;
    if (statistic === "mean") {
      const mean = values.reduce((sum, value) => sum + value, 0) / n;
      const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / Math.max(1, n - 1);
      const stdDev = Math.sqrt(Math.max(0, variance));
      return stdDev / Math.sqrt(n);
    }
    const sorted = [...values].sort((a, b) => a - b);
    const q1 = this.quantileFromSorted(sorted, 0.25);
    const q3 = this.quantileFromSorted(sorted, 0.75);
    const iqr = Math.max(0, q3 - q1);
    const robustSigma = iqr / 1.349;
    return (1.253 * robustSigma) / Math.sqrt(n);
  }

  private static buildLogStatisticEstimate(values: number[], statistic: JPBIndexStatistic): LogStatisticEstimate {
    const positiveValues = values.filter((value) => Number.isFinite(value) && value > 0);
    if (!positiveValues.length) {
      return {
        rawValue: null,
        logValue: null,
        standardError: null,
        txCount: 0,
      };
    }
    const logValues = positiveValues.map((value) => Math.log(value));
    const logValue = this.calculateStatistic(logValues, statistic);
    const standardError = this.calculateStatisticStandardError(logValues, statistic);
    return {
      rawValue:
        typeof logValue === "number" && Number.isFinite(logValue)
          ? Math.exp(logValue)
          : null,
      logValue:
        typeof logValue === "number" && Number.isFinite(logValue)
          ? logValue
          : null,
      standardError:
        typeof standardError === "number" && Number.isFinite(standardError)
          ? standardError
          : null,
      txCount: positiveValues.length,
    };
  }

  private static groupValuesByPeriodFromRows(
    rows: JPBRawTransactionRow[],
    periodKeys: Set<string>,
    interval: JPBIndexInterval,
    buildingAreaThresholdHa: number,
    pricePerM2Min: number | null,
    pricePerM2Max: number | null,
    subtractBuildingPrices: boolean,
    openIntervalStartMs: number
  ): Map<string, number[]> {
    const groupedValues = new Map<string, number[]>();
    rows.forEach((row) => {
      if (row.dato >= openIntervalStartMs) return;
      const value = this.toPricePerSquareMeter(row, buildingAreaThresholdHa, pricePerM2Min, pricePerM2Max, subtractBuildingPrices);
      if (value === null) return;
      const key = this.toPeriodKey(new Date(row.dato), interval);
      if (!periodKeys.has(key)) return;
      const existing = groupedValues.get(key);
      if (existing) {
        existing.push(value);
      } else {
        groupedValues.set(key, [value]);
      }
    });
    return groupedValues;
  }

  private static buildQuarterlyShrinkagePriorByPeriod(
    rowsByLandsdel: Map<string, JPBRawTransactionRow[]>,
    periodTemplate: PeriodTemplate[],
    buildingAreaThresholdHa: number,
    pricePerM2Min: number | null,
    pricePerM2Max: number | null,
    subtractBuildingPrices: boolean,
    statistic: JPBIndexStatistic,
    openIntervalStartMs: number
  ): Map<string, QuarterlyShrinkagePrior> {
    const periodKeys = new Set(periodTemplate.map((period) => period.key));
    const groupedByLandsdel = new Map<string, Map<string, number[]>>();
    rowsByLandsdel.forEach((rows, landsdel) => {
      groupedByLandsdel.set(
        landsdel,
        this.groupValuesByPeriodFromRows(
          rows,
          periodKeys,
          "quarter",
          buildingAreaThresholdHa,
          pricePerM2Min,
          pricePerM2Max,
          subtractBuildingPrices,
          openIntervalStartMs
        )
      );
    });

    const out = new Map<string, QuarterlyShrinkagePrior>();
    periodTemplate.forEach((period) => {
      const pooledValues: number[] = [];
      const regionalEstimates: Array<{ logValue: number; se2: number | null; weight: number }> = [];

      groupedByLandsdel.forEach((groupedValues) => {
        const values = groupedValues.get(period.key) || [];
        if (values.length) pooledValues.push(...values);
        const estimate = this.buildLogStatisticEstimate(values, statistic);
        const hasFiniteSe =
          typeof estimate.standardError === "number" && Number.isFinite(estimate.standardError);
        if (
          typeof estimate.logValue === "number" &&
          Number.isFinite(estimate.logValue) &&
          hasFiniteSe &&
          estimate.txCount >= SHRINKAGE_MIN_REGIONAL_TX_COUNT_FOR_TAU2
        ) {
          regionalEstimates.push({
            logValue: estimate.logValue,
            se2: Math.pow(estimate.standardError!, 2),
            weight: Math.max(1, estimate.txCount),
          });
        }
      });

      const pooledEstimate = this.buildLogStatisticEstimate(pooledValues, statistic);
      if (!(typeof pooledEstimate.logValue === "number" && Number.isFinite(pooledEstimate.logValue))) {
        out.set(period.key, { rawValue: null, logValue: null, tau2: null });
        return;
      }

      let tau2 = SHRINKAGE_MIN_TAU2;
      if (regionalEstimates.length >= 2) {
        const totalWeight = regionalEstimates.reduce((sum, item) => sum + item.weight, 0);
        const observedVariance =
          totalWeight > 0
            ? regionalEstimates.reduce(
                (sum, item) => sum + item.weight * Math.pow(item.logValue - pooledEstimate.logValue!, 2),
                0
              ) / totalWeight
            : 0;
        const averageSe2 =
          totalWeight > 0
            ? regionalEstimates.reduce(
                (sum, item) => sum + item.weight * (item.se2 ?? 0),
                0
              ) / totalWeight
            : 0;
        tau2 = Math.max(SHRINKAGE_MIN_TAU2, observedVariance - averageSe2);
      }

      out.set(period.key, {
        rawValue: pooledEstimate.rawValue,
        logValue: pooledEstimate.logValue,
        tau2,
      });
    });

    return out;
  }

  private static getValuesForDynamicBucketRules(
    periodTemplate: PeriodTemplate[],
    groupedValues: Map<string, number[]>,
    idx: number,
    statistic: JPBIndexStatistic
  ): DynamicWindowSelection {
    let fallback: DynamicWindowSelection = {
      values: [],
      rule: DYNAMIC_BUCKET_WINDOWS[0],
      relativeCi95: null,
    };
    for (let ruleIdx = 0; ruleIdx < DYNAMIC_BUCKET_WINDOWS.length; ruleIdx++) {
      const rule = DYNAMIC_BUCKET_WINDOWS[ruleIdx];
      const candidateValues = this.collectValuesInQuarterWindow(periodTemplate, groupedValues, idx, rule.halfWindowQuarters);
      const candidateStatistic = this.calculateStatistic(candidateValues, statistic);
      const relativeCi95 = this.estimateRelativeCi95(candidateValues, statistic, candidateStatistic);
      const normalizedRelativeCi95 =
        typeof relativeCi95 === "number" && Number.isFinite(relativeCi95) ? relativeCi95 : null;
      fallback = {
        values: candidateValues,
        rule,
        relativeCi95: normalizedRelativeCi95,
      };
      const hasEnoughTransactions = candidateValues.length >= DYNAMIC_BUCKET_MIN_TX_COUNT;
      const isStableEnough =
        typeof relativeCi95 === "number" &&
        Number.isFinite(relativeCi95) &&
        relativeCi95 <= DYNAMIC_BUCKET_MAX_RELATIVE_CI95;
      const isLastRule = ruleIdx === DYNAMIC_BUCKET_WINDOWS.length - 1;
      if ((hasEnoughTransactions && isStableEnough) || isLastRule) {
        return {
          values: candidateValues,
          rule,
          relativeCi95: normalizedRelativeCi95,
        };
      }
    }
    return fallback;
  }

  private static groupRowsByLandsdel(rows: JPBRawTransactionRow[]): Map<string, JPBRawTransactionRow[]> {
    const grouped = new Map<string, JPBRawTransactionRow[]>();
    rows.forEach((row) => {
      const list = grouped.get(row.landsdel);
      if (list) {
        list.push(row);
      } else {
        grouped.set(row.landsdel, [row]);
      }
    });
    return grouped;
  }

  private static getRowsForComputationLandsdel(
    landsdel: string,
    rowsByLandsdel: Map<string, JPBRawTransactionRow[]>,
    hovedstadenGroupingMode?: JPBHovedstadenGroupingMode
  ): JPBRawTransactionRow[] {
    if (landsdel !== HOVEDSTADEN_LANDSDEL) {
      return rowsByLandsdel.get(landsdel) || [];
    }

    const effectiveMode = hovedstadenGroupingMode || "none";
    const groupedLandsdele = HOVEDSTADEN_GROUPING_TO_LANDSDELE[effectiveMode] || HOVEDSTADEN_GROUPING_TO_LANDSDELE.none;
    const pooledRows: JPBRawTransactionRow[] = [];
    groupedLandsdele.forEach((memberLandsdel) => {
      const memberRows = rowsByLandsdel.get(memberLandsdel) || [];
      if (memberRows.length) pooledRows.push(...memberRows);
    });
    return pooledRows;
  }

  private static buildDocumentFromRows(
    landsdel: string,
    rows: JPBRawTransactionRow[],
    rowsByLandsdel: Map<string, JPBRawTransactionRow[]>,
    toTimestamp: number,
    options: JPBIndexBuilderOptions
  ): JPBIndexDocument {
    const computationMethod = this.resolveComputationMethod(options);
    const dynamicBucketGeneralization = this.resolveDynamicBucketGeneralization(options);
    const effectiveInterval = this.resolveComputationInterval(options);
    const effectiveSmoothingMode = this.resolveComputationSmoothingMode(options);
    const requestedYearsBack = Math.max(1, Math.floor(options.yearsBack || DEFAULT_YEARS_BACK));
    const fetchYearsBack = requestedYearsBack + 1;
    const { min: pricePerM2Min, max: pricePerM2Max } = this.getNormalizedPricePerM2Range(options.pricePerM2Min, options.pricePerM2Max);
    const documentBuildingAreaThresholdHa = this.getDocumentBuildingAreaThresholdHa(
      landsdel,
      options.buildingAreaThresholdHa,
      dynamicBucketGeneralization,
      options.hovedstadenGroupingMode
    );
    const openIntervalStartMs = this.getOpenIntervalStartMs(toTimestamp, effectiveInterval);
    const completedToTimestamp = openIntervalStartMs - 1;
    const toDate = new Date(completedToTimestamp);
    const computationFromDate = new Date(this.getFromTimestampForYearsBack(toTimestamp, fetchYearsBack));
    const outputFromDate = new Date(this.getFromTimestampForYearsBack(toTimestamp, requestedYearsBack));
    const computationPeriodTemplate = this.buildPeriodTemplate(computationFromDate, toDate, effectiveInterval);
    const outputPeriodTemplate = this.buildPeriodTemplate(outputFromDate, toDate, effectiveInterval);
    const computationPeriodKeys = new Set(computationPeriodTemplate.map((period) => period.key));
    const computationIdxByKey = new Map(computationPeriodTemplate.map((period, idx) => [period.key, idx]));
    const groupedValues = this.groupValuesByPeriodFromRows(
      rows,
      computationPeriodKeys,
      effectiveInterval,
      documentBuildingAreaThresholdHa,
      pricePerM2Min,
      pricePerM2Max,
      Boolean(options.subtractBuildingPrices),
      openIntervalStartMs
    );

    const periods: JPBIndexPeriod[] =
      computationMethod === "quarterlyShrinkage"
        ? (() => {
            const priorByPeriod = this.buildQuarterlyShrinkagePriorByPeriod(
              rowsByLandsdel,
              computationPeriodTemplate,
              documentBuildingAreaThresholdHa,
              pricePerM2Min,
              pricePerM2Max,
              Boolean(options.subtractBuildingPrices),
              options.statistic,
              openIntervalStartMs
            );
            return outputPeriodTemplate.map((period) => {
              const values = groupedValues.get(period.key) || [];
              const localEstimate = this.buildLogStatisticEstimate(values, options.statistic);
              const prior = priorByPeriod.get(period.key);
              const localLogValue = localEstimate.logValue;
              const priorLogValue = prior?.logValue ?? null;
              let value: number | null = null;
              let shrinkageWeight: number | null = null;
              const hasEnoughLocalTx = localEstimate.txCount >= SHRINKAGE_MIN_LOCAL_TX_COUNT;

              if (
                hasEnoughLocalTx &&
                typeof localLogValue === "number" &&
                Number.isFinite(localLogValue) &&
                typeof priorLogValue === "number" &&
                Number.isFinite(priorLogValue)
              ) {
                const localSe2 =
                  typeof localEstimate.standardError === "number" && Number.isFinite(localEstimate.standardError)
                    ? Math.pow(localEstimate.standardError, 2)
                    : Number.POSITIVE_INFINITY;
                const tau2 =
                  typeof prior?.tau2 === "number" && Number.isFinite(prior.tau2)
                    ? Math.max(SHRINKAGE_MIN_TAU2, prior.tau2)
                    : SHRINKAGE_MIN_TAU2;
                shrinkageWeight = Number.isFinite(localSe2) ? tau2 / (tau2 + localSe2) : 0;
                shrinkageWeight = Math.max(0, Math.min(1, shrinkageWeight));
                value = Math.exp(shrinkageWeight * localLogValue + (1 - shrinkageWeight) * priorLogValue);
              } else if (typeof prior?.rawValue === "number" && Number.isFinite(prior.rawValue)) {
                value = prior.rawValue;
                shrinkageWeight = 0;
              } else if (typeof localEstimate.rawValue === "number" && Number.isFinite(localEstimate.rawValue)) {
                value = localEstimate.rawValue;
                shrinkageWeight = 1;
              }

              return {
                periodKey: period.key,
                periodStart: period.start,
                periodEnd: period.end,
                value,
                factor: null,
                txCount: values.length,
                shrinkageWeight,
                shrinkageRawValue: localEstimate.rawValue,
                shrinkagePriorValue: prior?.rawValue ?? null,
              };
            });
          })()
        : outputPeriodTemplate.map((period) => {
            const computationIdx = computationIdxByKey.get(period.key);
            if (computationIdx === undefined) {
              return {
                periodKey: period.key,
                periodStart: period.start,
                periodEnd: period.end,
                value: null,
                factor: null,
                txCount: 0,
              };
            }
            const dynamicSelection = dynamicBucketGeneralization
              ? this.getValuesForDynamicBucketRules(computationPeriodTemplate, groupedValues, computationIdx, options.statistic)
              : null;
            const values = dynamicSelection
              ? dynamicSelection.values
              : this.getValuesForSmoothingMode(computationPeriodTemplate, groupedValues, computationIdx, effectiveSmoothingMode);
            const value = this.calculateStatistic(values, options.statistic);
            return {
              periodKey: period.key,
              periodStart: period.start,
              periodEnd: period.end,
              value,
              factor: null,
              txCount: values.length,
              dynamicBucketRule: dynamicSelection?.rule.name,
              dynamicHalfWindowQuarters: dynamicSelection?.rule.halfWindowQuarters,
              dynamicRelativeCi95: dynamicSelection ? dynamicSelection.relativeCi95 : undefined,
            };
          });

    const basePeriod = periods.find((p) => typeof p.value === "number" && p.value > 0) || null;
    const baseValue = basePeriod?.value ?? null;
    const periodsWithFactor = periods.map((period) => {
      if (baseValue && period.value && baseValue > 0) {
        return { ...period, factor: period.value / baseValue };
      }
      return { ...period, factor: null };
    });

    const doc: JPBIndexDocument = {
      landsdel,
      computationMethod,
      interval: effectiveInterval,
      statistic: options.statistic,
      smoothingMode: effectiveSmoothingMode,
      dynamicBucketGeneralization,
      hovedstadenGroupingMode:
        landsdel === HOVEDSTADEN_LANDSDEL ? (options.hovedstadenGroupingMode || "none") : undefined,
      buildingAreaThresholdHa: documentBuildingAreaThresholdHa,
      pricePerM2Min,
      pricePerM2Max,
      subtractBuildingPrices: Boolean(options.subtractBuildingPrices),
      yearsBack: requestedYearsBack,
      generatedAt: Date.now(),
      basePeriod: basePeriod?.periodKey ?? null,
      periods: periodsWithFactor,
    };

    if (options.includeFit) {
      const fit = this.computeExponentialFit(periodsWithFactor);
      if (fit) {
        doc.fitExp = fit;
      }
    }

    return doc;
  }

  private static toPricePerSquareMeter(
    row: JPBRawTransactionRow,
    buildingAreaThresholdHa: number,
    pricePerM2Min: number | null,
    pricePerM2Max: number | null,
    subtractBuildingPrices: boolean
  ): number | null {
    if (!(row.m2total > 0) || !(row.pris > 0)) {
      return null;
    }

    const hasBuildingValue =
      (typeof row.prisDriftsbygning === "number" && row.prisDriftsbygning > 0) ||
      (typeof row.prisStuehus === "number" && row.prisStuehus > 0);
    const thresholdM2 = Math.max(0, Number(buildingAreaThresholdHa) || 0) * 10000;
    const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || row.m2total >= thresholdM2;
    if (!buildingAreaOk) {
      return null;
    }

    const buildingsPriceSum = (row.prisDriftsbygning ?? 0) + (row.prisStuehus ?? 0);
    const effectiveRawPrice = subtractBuildingPrices ? Math.max(0, row.pris - buildingsPriceSum) : row.pris;
    if (!(effectiveRawPrice > 0)) {
      return null;
    }

    const effectivePricePerM2 = effectiveRawPrice / row.m2total;
    if (!this.isWithinPricePerM2Range(effectivePricePerM2, pricePerM2Min, pricePerM2Max)) {
      return null;
    }

    return effectivePricePerM2;
  }

  private static async getOrFetchRawRows(
    yearsBack: number,
    forceRefresh: boolean
  ): Promise<{ rows: JPBRawTransactionRow[]; cache: JPBRowsCache; usedCache: boolean }> {
    const sourceIndexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");
    const { date: referenceDate, fromMetadata } = await this.getReferenceDateInfo();
    const referenceDateMs = referenceDate.getTime();

    if (
      !forceRefresh &&
      this.rowsCache &&
      this.rowsCache.sourceIndexName === sourceIndexName &&
      this.rowsCache.yearsBack === yearsBack &&
      (!fromMetadata || this.rowsCache.referenceDateMs === referenceDateMs)
    ) {
      return { rows: this.rowsCache.rows, cache: { ...this.rowsCache }, usedCache: true };
    }

    const fetchYearsBack = yearsBack + 1;
    const fromTimestamp = this.getFromTimestampForYearsBack(referenceDateMs, fetchYearsBack);
    const toTimestamp = referenceDateMs;

    const rows = await this.fetchRawRowsInDateRange(sourceIndexName, fromTimestamp, toTimestamp);
    this.rowsCache = {
      rows,
      sourceIndexName,
      yearsBack,
      referenceDateMs,
      referenceFromMetadata: fromMetadata,
      fromTimestamp,
      toTimestamp,
      fetchedAt: Date.now(),
    };
    return { rows, cache: { ...this.rowsCache }, usedCache: false };
  }

  private static toCacheInfo(cache: JPBRowsCache, usedCache: boolean): JPBIndexBuilderCacheInfo {
    return {
      usedCache,
      sourceIndexName: cache.sourceIndexName,
      yearsBack: cache.yearsBack,
      referenceDateMs: cache.referenceDateMs,
      rowsInMemory: cache.rows.length,
      fromTimestamp: cache.fromTimestamp,
      toTimestamp: cache.toTimestamp,
      fetchedAt: cache.fetchedAt,
    };
  }

  private static async fetchRawRowsInDateRange(
    sourceIndexName: string,
    fromTimestamp: number,
    toTimestamp: number
  ): Promise<JPBRawTransactionRow[]> {
    const fromSeconds = Math.floor(fromTimestamp / 1000);
    const toSeconds = Math.floor(toTimestamp / 1000);
    const configuredFetchSize = Number(SettingsManager.getSystemSetting("jpb.indexBuilderFetchSize", DEFAULT_FETCH_SIZE));
    const fetchSize = Math.max(1000, Math.min(MAX_FETCH_SIZE, Number.isFinite(configuredFetchSize) ? configuredFetchSize : DEFAULT_FETCH_SIZE));
    const sourceEs = new ESInterfaceJWTAuth(sourceIndexName);
    const kommuneToLandsdel = this.getKommuneToLandsdelMap();
    const allRows: JPBRawTransactionRow[] = [];
    let page = 0;
    let searchAfter: unknown[] | undefined;
    let paginationRequired = false;

    while (page < MAX_PAGES) {
      page += 1;
      const query: any = {
        size: fetchSize,
        sort: [{ dato: "asc" }, { _seq_no: "asc" }],
        _source: [
          "dato",
          "pris",
          "m2total",
          "komkode",
          "prisDriftsbygning",
          "prisStuehus",
          "status",
          "reasonCode",
          "reasonText",
        ],
        query: {
          bool: {
            filter: [
              {
                bool: {
                  should: [
                    {
                      range: {
                        dato: {
                          gte: fromTimestamp,
                          lte: toTimestamp,
                        },
                      },
                    },
                    {
                      range: {
                        dato: {
                          gte: fromSeconds,
                          lte: toSeconds,
                        },
                      },
                    },
                  ],
                  minimum_should_match: 1,
                },
              },
            ],
          },
        },
      };

      if (searchAfter && searchAfter.length > 0) {
        query.search_after = searchAfter;
      }

      const response = await sourceEs.doQuery(query);
      const hits = (response?.hits?.hits || []) as JPBHit[];
      if (!hits.length) {
        break;
      }

      hits.forEach((hit) => {
        const src = hit._source;
        const rawDato = src?.dato;
        let dato = typeof rawDato === "number" ? rawDato : NaN;
        if (Number.isFinite(dato) && dato > 0 && dato < 10_000_000_000) {
          dato *= 1000;
        }
        const price = src?.pris;
        const area = src?.m2total;
        const normalizedKommune = this.normalizeKommuneCode(src?.komkode);
        const landsdel = normalizedKommune ? kommuneToLandsdel.get(normalizedKommune) : undefined;
        const issueText = JPBManager.getTransactionIssueText(
          src?.status,
          src?.reasonText,
          src?.reasonCode
        );
        if (
          issueText ||
          !landsdel ||
          typeof price !== "number" ||
          !(price > 0) ||
          typeof area !== "number" ||
          !(area > 0) ||
          typeof dato !== "number" ||
          !(dato > 0)
        ) {
          return;
        }

        allRows.push({
          dato,
          pris: price,
          m2total: area,
          komkode: normalizedKommune!,
          landsdel,
          prisDriftsbygning: this.toPositiveNumberOrNull(src?.prisDriftsbygning),
          prisStuehus: this.toPositiveNumberOrNull(src?.prisStuehus),
        });
      });

      if (hits.length < fetchSize) {
        break;
      }
      paginationRequired = true;

      const lastHit = hits[hits.length - 1];
      if (lastHit?.sort && lastHit.sort.length > 0) {
        searchAfter = lastHit.sort;
      } else if (lastHit?._source?.dato && typeof lastHit._seq_no === "number") {
        searchAfter = [lastHit._source.dato, lastHit._seq_no];
      } else {
        break;
      }
    }

    if (paginationRequired && allRows.length > fetchSize) {
      console.info(`JPBIndexBuilder: paginated all-landsdele fetch used, rows=${allRows.length}`);
    }

    return allRows;
  }

  private static toPositiveNumberOrNull(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
  }

  private static normalizeKommuneCode(value: unknown): string | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(Math.trunc(value));
    }
    if (typeof value === "string") {
      const parsed = Number(value.trim());
      if (Number.isFinite(parsed)) {
        return String(Math.trunc(parsed));
      }
    }
    return undefined;
  }
}

